import * as bootstrap from 'bootstrap';
import { appState } from './appState';
import { renderByokKeys, renderModelDropdown } from './byok';
import { loadChat, renderChats, renderProjects, resetProjectSelection } from './chat';
import { t } from './i18n';
import { refreshProjectSettingsContext } from './settings';
import { store } from './store';
import { storage } from './storage';
import { updateUI } from './ui';
import { SubscriptionLevel } from './types';
const TOKEN_KEY = 'blindquery_auth_token';
function getStoredToken() {
    return localStorage.getItem(TOKEN_KEY);
}
function setStoredToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
}
function clearStoredToken() {
    localStorage.removeItem(TOKEN_KEY);
}
function normalizePlan(plan) {
    if (!plan) {
        return SubscriptionLevel.FREE;
    }
    return plan.toLowerCase() === 'enterprise' ? SubscriptionLevel.ENTERPRISE : SubscriptionLevel.FREE;
}
function mapBackendUser(payload) {
    return {
        id: String(payload.id ?? ''),
        name: String(payload.name ?? ''),
        email: String(payload.email ?? ''),
        level: normalizePlan(payload.plan)
    };
}
async function apiFetch(input, init = {}) {
    const token = getStoredToken();
    const headers = new Headers(init.headers || undefined);
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    return fetch(input, {
        ...init,
        headers,
        credentials: 'include'
    });
}
async function fetchUserStorageKey() {
    const response = await apiFetch('/api/auth/key', { method: 'GET' });
    if (!response.ok) {
        throw new Error('Failed to fetch user encryption key');
    }
    const payload = await response.json();
    if (!payload || typeof payload.key !== 'string') {
        throw new Error('Invalid encryption key response');
    }
    return payload.key;
}
export async function initializeEncryptionForUser(user) {
    if (!user) {
        // For anonymous users, use a default encryption key for local storage
        storage.setUserKey('blindquery-anonymous-key');
        return true;
    }
    try {
        const key = await fetchUserStorageKey();
        storage.setUserKey(key);
        return true;
    }
    catch (error) {
        console.warn('Falling back to test encryption key', error);
        const fallbackKey = `test-${user.id}-${user.email}`;
        storage.setUserKey(fallbackKey);
        return true;
    }
}
async function applyAuthenticatedUser(user) {
    const hasKey = await initializeEncryptionForUser(user);
    if (!hasKey) {
        alert(t('alerts.encryptionKeyFailed'));
        return;
    }
    await store.initialize(user);
    store.setUser(user);
    resetProjectSelection();
    renderProjects();
    renderChats();
    await loadChat();
    await refreshProjectSettingsContext();
    const modalElement = document.getElementById('loginModal');
    const modal = modalElement ? bootstrap.Modal.getInstance(modalElement) : null;
    modal?.hide();
    updateUI();
    renderByokKeys();
    renderModelDropdown();
}
export async function handleFakeLogin(provider) {
    const user = {
        id: crypto.randomUUID(),
        name: t('sidebar.enterpriseUser'),
        email: 'user@enterprise.com',
        level: SubscriptionLevel.FREE
    };
    if (provider === 'apple') {
        user.name = 'John Enterprise';
        user.email = 'john@enterprise.com';
        user.level = SubscriptionLevel.ENTERPRISE;
    }
    await applyAuthenticatedUser(user);
}
export async function handleSocialLogin(provider) {
    const redirectBase = `${window.location.origin}${window.location.pathname}`;
    const redirectUrl = encodeURIComponent(redirectBase);
    window.location.href = `/api/auth/${provider}/redirect?redirect=${redirectUrl}`;
}
function extractTokenFromUrl() {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) {
        return null;
    }
    const params = new URLSearchParams(hash);
    const token = params.get('token');
    return token || null;
}
function clearTokenFromUrl() {
    if (window.location.hash) {
        history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }
}
async function fetchCurrentUser() {
    try {
        const response = await apiFetch('/api/auth/me', { method: 'GET' });
        if (!response.ok) {
            console.log('[Auth] fetchCurrentUser: response not OK, status:', response.status);
            return null;
        }
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            console.log('[Auth] fetchCurrentUser: not JSON response, content-type:', contentType);
            return null;
        }
        const payload = await response.json();
        if (!payload || !payload.user) {
            return null;
        }
        return mapBackendUser(payload.user);
    }
    catch (error) {
        console.log('[Auth] fetchCurrentUser: error:', error);
        return null;
    }
}
export async function bootstrapAuthSession() {
    const tokenFromUrl = extractTokenFromUrl();
    if (tokenFromUrl) {
        setStoredToken(tokenFromUrl);
        clearTokenFromUrl();
    }
    const token = getStoredToken();
    if (!token) {
        store.clearSession();
        return null;
    }
    const user = await fetchCurrentUser();
    if (!user) {
        clearStoredToken();
        store.clearSession();
        return null;
    }
    await applyAuthenticatedUser(user);
    return user;
}
export async function handleLogout() {
    const token = getStoredToken();
    if (token) {
        await apiFetch('/api/auth/logout', { method: 'POST' });
    }
    clearStoredToken();
    storage.clearUserKey();
    store.clearSession();
    appState.currentProjectId = null;
    appState.currentChatId = null;
    renderProjects();
    renderChats();
    void loadChat();
    void refreshProjectSettingsContext();
    updateUI();
    renderByokKeys();
    renderModelDropdown();
}
export function handleUpgrade() {
    const user = store.getUser();
    if (user) {
        user.level = SubscriptionLevel.ENTERPRISE;
        store.setUser(user);
    }
    const modalElement = document.getElementById('paywallModal');
    const modal = modalElement ? bootstrap.Modal.getInstance(modalElement) : null;
    modal?.hide();
    updateUI();
}
